/**
 * useDeckNeeds - Hook for computing cards needed across all decks
 *
 * Aggregates all cards across brewing and active decks, compares against
 * collection quantities, and computes shortfall per card grouped by deck.
 *
 * Maybeboard entries are wishlist noise and never count.
 *
 * Both hooks answer the same question — how many more copies must exist for the
 * scope to be satisfiable — but scope contention differently. `useDeckNeeds`
 * sums every brewing and active deck, so decks competing for one copy surface
 * naturally. `useDeckNeedsForDeck` looks at a single deck, so it subtracts
 * copies already committed to *other* active decks; brewing decks reserve
 * nothing, matching the availability model in `state/cardAvailability.ts`.
 */
import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { allDeckCardsAtom } from '@/features/decks/state/allDeckCards';
import { decksStateAtom } from '@/features/decks/state/decksAtom';
import useUserSettings from '@/features/settings/hooks/api';
import { useCardLookup } from '@/hooks/useCardLookup';
import { getSimplePriceFromPrices } from '@/lib/price';
import { availabilityCardsByScryfallIdAtom, collectionOwnedByOracleIdAtom, deckUsageByNameAtom } from '@/state/cardAvailability';
import type { Deck, DeckCard } from '@/types/deck';
import type { DeckNeedCard, DeckNeedSource } from '@/types/lists';

// ============================================================================
// Types
// ============================================================================

export interface DeckNeedsResult {
  /** All cards that have a shortfall (needed > owned) */
  cards: DeckNeedCard[];
  /** Total cards needed (sum of all shortfalls) */
  totalNeeded: number;
  /** Estimated total value of needed cards */
  totalValue: number;
  /** Number of unique cards needed */
  uniqueCardsNeeded: number;
  /** Whether data is still loading */
  isLoading: boolean;
}

interface AggregatedNeed {
  oracleId: string;
  name: string;
  /** Lowercase name, since deck usage is tracked by name (printings are interchangeable) */
  nameLower: string;
  /** Any printing referenced by a deck, used only for price lookup */
  scryfallId: string;
  total: number;
  /** deck id -> quantity */
  deckSources: Map<string, number>;
}

interface BuildNeedsContext {
  deckById: Map<string, Deck>;
  getOracleId: (scryfallId: string) => string | null;
  getNameLower: (scryfallId: string) => string | null;
  ownedByOracleId: Map<string, number>;
  /** Copies held by active decks outside the scope; always 0 for the cross-deck view. */
  committedElsewhere: (nameLower: string) => number;
  getCardName: (scryfallId: string) => string | null;
  getPrice: (scryfallId: string) => number | null;
}

// ============================================================================
// Aggregation
// ============================================================================

/**
 * Shared aggregation for both hooks so the zone and deck-status rules cannot drift.
 * Oracle IDs resolve through the same lookup as the availability features, which
 * handles reversible faces and falls back to the minimal index.
 */
function aggregateNeeds(deckCards: DeckCard[], context: BuildNeedsContext): Map<string, AggregatedNeed> {
  const { deckById, getOracleId, getNameLower, getCardName } = context;
  const aggregated = new Map<string, AggregatedNeed>();

  for (const deckCard of deckCards) {
    if (!deckById.has(deckCard.deck_id)) continue;
    if (deckCard.zone === 'maybeboard') continue;

    const oracleId = getOracleId(deckCard.scryfall_id);
    if (!oracleId) continue;

    let entry = aggregated.get(oracleId);
    if (!entry) {
      const name = getCardName(deckCard.scryfall_id);
      const nameLower = getNameLower(deckCard.scryfall_id);
      if (!name || !nameLower) continue;
      entry = { oracleId, name, nameLower, scryfallId: deckCard.scryfall_id, total: 0, deckSources: new Map() };
      aggregated.set(oracleId, entry);
    }

    entry.total += deckCard.quantity;
    entry.deckSources.set(deckCard.deck_id, (entry.deckSources.get(deckCard.deck_id) ?? 0) + deckCard.quantity);
  }

  return aggregated;
}

function buildNeedCards(aggregated: Map<string, AggregatedNeed>, context: BuildNeedsContext) {
  const { deckById, ownedByOracleId, committedElsewhere, getPrice } = context;
  const cards: DeckNeedCard[] = [];
  let totalNeeded = 0;
  let totalValue = 0;

  for (const entry of aggregated.values()) {
    const owned = ownedByOracleId.get(entry.oracleId) ?? 0;
    const free = Math.max(0, owned - committedElsewhere(entry.nameLower));
    const shortfall = Math.max(0, entry.total - free);
    if (shortfall <= 0) continue;

    const decks: DeckNeedSource[] = [];
    for (const [deckId, quantity] of entry.deckSources) {
      const deck = deckById.get(deckId);
      if (deck) decks.push({ deck_id: deckId, deck_name: deck.name, quantity });
    }
    decks.sort((a, b) => b.quantity - a.quantity);

    const cheapestPrice = getPrice(entry.scryfallId);

    cards.push({
      oracle_id: entry.oracleId,
      name: entry.name,
      total_needed: entry.total,
      owned,
      shortfall,
      decks,
      cheapest_price: cheapestPrice,
    });

    totalNeeded += shortfall;
    if (cheapestPrice != null) totalValue += cheapestPrice * shortfall;
  }

  cards.sort((a, b) => {
    if (b.shortfall !== a.shortfall) return b.shortfall - a.shortfall;
    return a.name.localeCompare(b.name);
  });

  return { cards, totalNeeded, totalValue };
}

/**
 * Builds the lookup helpers both hooks need from the shared availability atoms.
 *
 * @param scopeDeckId - Deck the needs are scoped to, or null to aggregate across decks.
 *   When set, copies committed to other active decks are treated as unavailable.
 */
function useNeedsContext(deckById: Map<string, Deck>, scopeDeckId: string | null): BuildNeedsContext {
  const availabilityCards = useAtomValue(availabilityCardsByScryfallIdAtom);
  const ownedByOracleId = useAtomValue(collectionOwnedByOracleIdAtom);
  const usageByName = useAtomValue(deckUsageByNameAtom);
  const { getCard, getFullCard } = useCardLookup();
  const { settings } = useUserSettings();
  const currency = settings.currency;

  return useMemo(
    () => ({
      deckById,
      getOracleId: (scryfallId: string) => availabilityCards.get(scryfallId)?.oracle_id ?? null,
      getNameLower: (scryfallId: string) => availabilityCards.get(scryfallId)?.name_lower ?? null,
      ownedByOracleId,
      committedElsewhere: (nameLower: string) => {
        if (!scopeDeckId) return 0;
        let total = 0;
        for (const [deckId, quantity] of usageByName.get(nameLower)?.committed ?? []) {
          if (deckId !== scopeDeckId) total += quantity;
        }
        return total;
      },
      getCardName: (scryfallId: string) => getCard(scryfallId)?.name ?? null,
      getPrice: (scryfallId: string) => getSimplePriceFromPrices(getFullCard(scryfallId)?.prices, currency),
    }),
    [deckById, scopeDeckId, availabilityCards, ownedByOracleId, usageByName, getCard, getFullCard, currency],
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook that computes cards needed across all decks.
 *
 * @returns Aggregated deck needs with shortfall calculations
 */
export function useDeckNeeds(): DeckNeedsResult {
  const allDeckCards = useAtomValue(allDeckCardsAtom);
  const decks = useAtomValue(decksStateAtom);

  const deckById = useMemo(
    () => new Map((decks.data ?? []).filter((d) => d.status === 'brewing' || d.status === 'active').map((d) => [d.id, d])),
    [decks.data],
  );
  const context = useNeedsContext(deckById, null);

  return useMemo(() => {
    const isLoading = allDeckCards.isLoading || decks.isLoading;

    if (!allDeckCards.data || !decks.data || isLoading) {
      return { cards: [], totalNeeded: 0, totalValue: 0, uniqueCardsNeeded: 0, isLoading };
    }

    const { cards, totalNeeded, totalValue } = buildNeedCards(aggregateNeeds(allDeckCards.data, context), context);

    return { cards, totalNeeded, totalValue, uniqueCardsNeeded: cards.length, isLoading: false };
  }, [allDeckCards.data, allDeckCards.isLoading, decks.data, decks.isLoading, context]);
}

/**
 * Hook that computes deck needs for a specific deck.
 *
 * @param deckId - The deck ID to compute needs for
 * @returns Cards needed for this specific deck
 */
export function useDeckNeedsForDeck(deckId: string): DeckNeedCard[] {
  const allDeckCards = useAtomValue(allDeckCardsAtom);
  const decks = useAtomValue(decksStateAtom);

  const deckById = useMemo(() => {
    const deck = (decks.data ?? []).find((d) => d.id === deckId);
    return deck ? new Map([[deck.id, deck]]) : new Map<string, Deck>();
  }, [decks.data, deckId]);
  const context = useNeedsContext(deckById, deckId);

  return useMemo(() => {
    if (!allDeckCards.data || deckById.size === 0) return [];
    return buildNeedCards(aggregateNeeds(allDeckCards.data, context), context).cards;
  }, [allDeckCards.data, deckById, context]);
}
